import { FunctionComponent, useState } from "react";
import DuplicateIcon from "@static/core/icons/icon-duplicate.svg?react";
import InfoModal from "@components/core/modals/InfoModal";
import BasicModal from "@components/core/modals/BasicModal";
import { Input } from "antd";
import BasicButton from "@components/core/buttons/BasicButton";
import { useTranslation } from "react-i18next";
import { TransactionDetail } from "@type/core/transaction/transaction.types";
import { requestDuplicateTransaction } from "@state/transactions/TransactionEffects";
import { toastError } from "@utils/helpers/toast-helper";
import { Office } from "@type/core/offices/offices.types";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "@routes/Routes";

interface DuplicateButtonProps {
  shouldShowInfo: boolean;
  hasDataOrigin: boolean;
  transaction: TransactionDetail;
  office: Office;
  className?: string;
}

const DuplicateButton: FunctionComponent<DuplicateButtonProps> = ({
  shouldShowInfo,
  hasDataOrigin,
  transaction,
  office,
  className = "",
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const copy = `${transaction.object}-copie`;
  const [loading, setLoading] = useState<boolean>(false);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] =
    useState<boolean>(false);
  const [object, setObject] = useState<string>(copy.substring(0, 45));

  const showDocumentText = !hasDataOrigin;

  return (
    <>
      <div className={`d-flex align-items-center gap-4 ${className}`}>
        <DuplicateIcon
          className="text-primary cursor-pointer icon-30"
          onClick={() => setIsDuplicateModalOpen(true)}
        />
        {shouldShowInfo && <InfoModal value="duplicated" info />}
      </div>
      <BasicModal
        visible={isDuplicateModalOpen}
        onCancel={() => setIsDuplicateModalOpen(false)}
        title={
          <div className="d-flex align-items-center gap-8">
            {t("duplicated.modal.title")}
            {shouldShowInfo && <InfoModal value="duplicated" info />}
          </div>
        }
        closable={false}
        footer={
          <div className="modal-footer-buttons">
            <BasicButton
              disabled={loading}
              variant="secondary"
              text={t("buttons.cancel")}
              onClick={() => setIsDuplicateModalOpen(false)}
            />
            <BasicButton
              disabled={loading}
              isLoading={loading}
              variant="primary"
              text={t("buttons.validate")}
              onClick={() => {
                setIsDuplicateModalOpen(false);
                setLoading(true);
                void requestDuplicateTransaction({
                  id: transaction.id,
                  dto: {
                    reference: object,
                    type: transaction.type ?? "",
                    office: office.id,
                  },
                }).then((result) => {
                  if (result.ok && result.data) {
                    navigate(
                      ROUTES.extranet.transactions.details.generate(
                        result.data.id,
                      ),
                    );
                    setLoading(false);
                  } else {
                    toastError(
                      "Erreur lors de la duplication de la transaction",
                    );
                  }
                });
              }}
            />
          </div>
        }
        className={`app-modal- ${className}`}
      >
        <>
          <label htmlFor="object" className="d-inline-block mb-8">
            {t("duplicated.modal.title")}
          </label>
          <Input
            disabled={loading}
            id="object"
            value={object}
            onChange={(e) => setObject(e.target.value)}
            maxLength={45}
            className="mb-16"
          />

          {showDocumentText && (
            <div className="mb-16">
              <div className="text-danger">
                {t("duplicated.modal.documents")}
              </div>
            </div>
          )}
        </>
      </BasicModal>
    </>
  );
};

export default DuplicateButton;
